import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

/**
 * Android hardware Back behavior for authenticated layouts:
 * - drill-down: browser history unwinds exactly one level
 * - report root: returns to the dashboard (guard entry below it)
 * - dashboard: exits the native app
 * Browser/right-side Back buttons keep using the same history stack.
 */
export default function useNativeBackGuard(dashboardPath) {
  const location = useLocation();

  useEffect(() => {
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (window.location.pathname === dashboardPath) return;

    const state = window.history.state || {};
    if (state.kgNativeBackGuardFor === currentPath) return;

    // Put a dashboard entry directly below each report root. Drill screens add
    // their own pushState entries above this, so Back naturally unwinds them.
    window.history.replaceState({ ...state, kgNativeBackFallback: true }, "", dashboardPath);
    window.history.pushState({ ...state, kgNativeBackGuardFor: currentPath }, "", currentPath);
  }, [dashboardPath, location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let handle;
    let cancelled = false;

    App.addListener("backButton", () => {
      const onDashboard = window.location.pathname === dashboardPath;
      const state = window.history.state || {};

      // Client dashboard drill-downs stay on /client/dashboard and mark each
      // drill history entry with kgClientDrill.  Do not exit while such an
      // entry is active; unwind exactly one drill level instead.
      if (onDashboard && state.kgClientDrill) {
        window.history.back();
        return;
      }

      if (onDashboard) {
        App.exitApp();
        return;
      }

      // Normal report routes return through the browser/router history.
      window.history.back();
    }).then((h) => {
      if (cancelled) h.remove();
      else handle = h;
    });

    return () => {
      cancelled = true;
      if (handle) handle.remove();
    };
  }, [dashboardPath]);
}
